import { useMemo } from "react";
import { Trans } from "@lingui/react/macro";
import OrderItem, { MergedOrderItem } from "./Common/OrderItem";
import { useOrders } from "../utils/hooks/useOrders";
import { useRouter } from "next/router";
import appRoutes from "../utils/constants/appRoutes";
import {
  Button,
  Card,
  CardContent,
  Heading,
  Text,
  VStack,
} from "@wootingkb/component-library";
import NextLink from "next/link";

interface OHProps {
  limitWeight?: boolean;
}

export default function OrderHistory({ limitWeight }: OHProps) {
  const router = useRouter();
  const { orders, hasOrders } = useOrders();

  const { displayOrders, listIsComplete } = useMemo(() => {
    if (!orders) {
      return { displayOrders: undefined, listIsComplete: true };
    }

    if (!limitWeight) {
      return { displayOrders: orders, listIsComplete: true };
    }

    let totalWeight = 0;
    const weightThreshold = 7;
    const newOrders = orders.filter((order) => {
      const orderWeight = (order?.merged_orders?.length ?? 0) + 1;
      totalWeight += orderWeight;
      return totalWeight < weightThreshold;
    });

    return {
      displayOrders: newOrders,
      listIsComplete: orders.length === newOrders.length,
    };
  }, [orders, limitWeight]);

  return (
    <VStack className="mb-16 gap-4">
      {/* Placeholder, displayed if no order has loaded yet. */}
      {!orders && <OrderItem order={undefined} />}

      {displayOrders?.map((order, index) => {
        return (
          <VStack className="pb-6" key={index}>
            {order.merged_orders ? (
              <OrderItem order={order}>
                {order.merged_orders.map((merged_order, index) => (
                  <MergedOrderItem
                    order={merged_order}
                    isLastElement={order?.merged_orders?.length === index + 1}
                    key={index}
                  />
                ))}
              </OrderItem>
            ) : (
              <OrderItem order={order} />
            )}
          </VStack>
        );
      })}

      {hasOrders === false && (
        <Card>
          <CardContent className="gap-2">
            <Heading size="h5" weight="bold">
              <Trans id="OrderHistory_no_orders_title">
                There are no orders in your account.
              </Trans>
            </Heading>
            <Text>
              <Trans id="OrderHistory_no_orders_processing_delay">
                Just placed your order? It can take up to an hour to show up on
                your account!
              </Trans>
            </Text>
            {router.pathname === appRoutes.MY_ORDERS ? (
              <Text>
                <Trans id="OrderHistory_transfer_hint_orders_page">
                  Click the Request Order Transfer button to request to transfer
                  an order from another account.
                </Trans>
              </Text>
            ) : (
              <Text>
                <Trans id="OrderHistory_transfer_hint_other_page">
                  Go to{" "}
                  <NextLink href="/my/orders" className="italic underline">
                    your orders
                  </NextLink>{" "}
                  to request to transfer an order from another account.
                </Trans>
              </Text>
            )}
          </CardContent>
        </Card>
      )}

      {!listIsComplete && (
        <NextLink href="/my/orders">
          <Button variant="muted">
            <Trans id="OrderHistory_view_more_orders">View more orders</Trans>
          </Button>
        </NextLink>
      )}
    </VStack>
  );
}
